import { Pencil, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { deleteSupplier } from '@/data/suppliersRepo';
import { useExperiences } from '@/hooks/useExperiences';
import { useSuppliers } from '@/hooks/useSuppliers';
import { normaliseSupplierClosures, type SupplierType } from '@/types/types_supplier';
import { todayCalendarDate } from '@/util/util_format';

/** Admin list of suppliers: who runs the experiences, and when they are away. */
export function PageSupplierList() {
  const { suppliers, loading } = useSuppliers();
  // The catalogue, for the one number this page is really about: how many
  // experiences a supplier's absences close. It is also what decides whether a
  // supplier can be deleted, so it is worth the subscription.
  const { experiences } = useExperiences();
  const [toDelete, setToDelete] = useState<SupplierType | null>(null);
  const [deleting, setDeleting] = useState(false);
  const today = todayCalendarDate();

  function experiencesFor(supplierId: string) {
    return experiences.filter((e) => e.supplierId === supplierId);
  }

  async function confirmDelete() {
    if (!toDelete) return;
    setDeleting(true);
    try {
      await deleteSupplier(toDelete.id);
      setToDelete(null);
      // The snapshot listener removes the row automatically.
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div>
      <header className='tw:mb-6 tw:flex tw:items-center tw:justify-between'>
        <div>
          <h1 className='tw:font-serif tw:text-2xl tw:text-ed-black'>Suppliers</h1>
          <p className='tw:mt-1 tw:text-sm tw:text-ed-stone'>
            The artisans and houses that run the experiences. Their days away are typed here once and
            apply to every experience linked to them. {suppliers.length} suppliers.
          </p>
        </div>
        <Link
          to='/admin/suppliers/new'
          className='tw:flex tw:items-center tw:gap-2 tw:bg-ed-gold tw:px-4 tw:py-2 tw:text-sm tw:font-medium tw:text-ed-black tw:transition-colors tw:hover:bg-ed-black tw:hover:text-ed-cream'
        >
          <Plus className='tw:size-4' /> Add Supplier
        </Link>
      </header>

      <div className='tw:overflow-x-auto tw:rounded-sm tw:border tw:border-ed-umber/10 tw:bg-white tw:shadow-sm'>
        <table className='tw:w-full'>
          <thead className='tw:bg-ed-cream/50'>
            <tr>
              {['Supplier', 'Experiences', 'Days away', ''].map((h) => (
                <th
                  key={h}
                  className='tw:px-6 tw:py-3 tw:text-left tw:text-xs tw:font-medium tw:tracking-wider tw:text-ed-stone tw:uppercase'
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={4} className='tw:px-6 tw:py-10 tw:text-center tw:text-ed-stone'>
                  Loading…
                </td>
              </tr>
            ) : suppliers.length === 0 ? (
              <tr>
                <td colSpan={4} className='tw:px-6 tw:py-10 tw:text-center tw:text-ed-stone'>
                  No suppliers yet.
                </td>
              </tr>
            ) : (
              suppliers.map((supplier) => {
                const linked = experiencesFor(supplier.id);
                // Absences already over are not the number anybody wants; they
                // stay on the record, but the column is about what is still to
                // come.
                const upcoming = normaliseSupplierClosures(supplier).filter((p) => p.endDate >= today);
                return (
                  <tr key={supplier.id} className='tw:border-t tw:border-ed-umber/10 tw:hover:bg-ed-cream/20'>
                    <td className='tw:px-6 tw:py-4 tw:text-sm tw:font-medium tw:text-ed-black'>
                      {supplier.name || supplier.id}
                      {supplier.notes && (
                        <div className='tw:max-w-md tw:truncate tw:text-xs tw:text-ed-stone'>{supplier.notes}</div>
                      )}
                    </td>
                    <td className='tw:px-6 tw:py-4 tw:text-sm tw:text-ed-charcoal'>
                      {linked.length === 0 ? (
                        <span className='tw:text-ed-stone'>None linked</span>
                      ) : (
                        <span title={linked.map((e) => e.name).join(', ')}>
                          {linked.length} {linked.length === 1 ? 'experience' : 'experiences'}
                        </span>
                      )}
                    </td>
                    <td className='tw:px-6 tw:py-4 tw:text-sm tw:text-ed-charcoal'>
                      {upcoming.length === 0 ? (
                        <span className='tw:text-ed-stone'>—</span>
                      ) : (
                        `${upcoming.length} upcoming`
                      )}
                    </td>
                    <td className='tw:px-6 tw:py-4'>
                      <div className='tw:flex tw:justify-end tw:gap-2'>
                        <Link
                          to={`/admin/suppliers/${supplier.id}/edit`}
                          title='Edit'
                          className='tw:border tw:border-ed-umber/30 tw:p-2 tw:text-ed-charcoal tw:transition-colors tw:hover:border-ed-gold tw:hover:text-ed-gold'
                        >
                          <Pencil className='tw:size-4' />
                        </Link>
                        <button
                          type='button'
                          title='Delete'
                          onClick={() => setToDelete(supplier)}
                          className='tw:border tw:border-ed-umber/30 tw:p-2 tw:text-ed-charcoal tw:transition-colors tw:hover:border-ed-red tw:hover:text-ed-red'
                        >
                          <Trash2 className='tw:size-4' />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <DeleteSupplierDialog
        supplier={toDelete}
        linkedNames={toDelete ? experiencesFor(toDelete.id).map((e) => e.name) : []}
        deleting={deleting}
        onCancel={() => setToDelete(null)}
        onConfirm={() => void confirmDelete()}
      />
    </div>
  );
}

/**
 * Deleting a supplier, and refusing to while experiences still point at it.
 *
 * Refused rather than cascaded: unlinking them here would mean editing several
 * experiences as a side effect of a click on this page — each one a real edit
 * with its own history step, on records the admin did not open. Naming them
 * instead leaves the decision where it belongs, on the experience.
 */
function DeleteSupplierDialog({
  supplier,
  linkedNames,
  deleting,
  onCancel,
  onConfirm,
}: {
  supplier: SupplierType | null;
  linkedNames: string[];
  deleting: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const linked = linkedNames.length > 0;

  return (
    <Dialog open={!!supplier} onOpenChange={(open) => !open && !deleting && onCancel()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{linked ? 'Still in use' : 'Delete supplier?'}</DialogTitle>
          <DialogDescription>
            {linked ? (
              <>
                &ldquo;{supplier?.name}&rdquo; runs {linkedNames.length}{' '}
                {linkedNames.length === 1 ? 'experience' : 'experiences'}: {linkedNames.join(', ')}. Change
                the supplier on {linkedNames.length === 1 ? 'it' : 'each of them'} first, then delete this
                record.
              </>
            ) : (
              <>
                This will remove &ldquo;{supplier?.name}&rdquo; and the days away recorded on it. This
                cannot be undone.
              </>
            )}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant='outline' disabled={deleting} onClick={onCancel}>
            {linked ? 'Close' : 'Cancel'}
          </Button>
          {!linked && (
            <Button variant='destructive' loading={deleting} onClick={onConfirm}>
              Delete
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
